import { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import { Link } from "@chakra-ui/react";
import { HistoricalReportsModal } from "features/reports/historical-reports/HistoricalReportsModal";
import { useHistoricalReportTableRetrieveSuspense } from "gen/endpoints/reports/reports";
import { GenericTableRow } from "gen/model/genericTableRow";
import { ReportingRunParametersRowMetadata } from "gen/model/reportingRunParametersRowMetadata";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentDownloadLink } from "shared/components/link/DownloadDocumentLink";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import {
  DataTableAgHeaderComponentParams,
  GetColumnDefFn,
} from "shared/components/table/data/DataTableTypes";
import { FileDownloadIcon } from "shared/icons/FileDownloadIcon";
import { match } from "ts-pattern";

type ReportInfo = {
  ranBy: string;
  ranOn: string;
  parameters: ReportingRunParametersRowMetadata;
  type: string;
};

const extraColumnProps: ColDef = {
  flex: 0,
  lockPinned: true,
  lockPosition: "right",
  pinned: "right",
  suppressMovable: true,
  sortable: false,
  resizable: false,
  suppressSizeToFit: true,
  headerComponentParams: {},
  headerClass: "mb-header-cell-centered",
  cellClass: "mb-cell-centered",
};

export const HistoricalReportsTable: React.FC = () => {
  const { t } = useTranslation(["reports", "common"]);
  const { data } = useHistoricalReportTableRetrieveSuspense();

  const [currentReport, setCurrentReport] = useState<ReportInfo | null>(null);

  const extraColumns = useMemo(
    (): ColDef[] => [
      {
        ...extraColumnProps,
        colId: "viewParameters",
        headerName: t("reports:historicalTable.viewParameters"),
        headerComponentParams: {
          column: {
            id: "viewParameters",
          },
        } as DataTableAgHeaderComponentParams,
        cellRenderer: ({
          data,
        }: ICellRendererParams<GenericTableRow, string, DataTableContext>) => {
          const reportInfo = reportInfoFromRow(data);
          return reportInfo ? (
            <Link
              colorScheme="gray"
              onClick={() => {
                setCurrentReport(reportInfo);
              }}
            >
              {t("common:view")}
            </Link>
          ) : undefined;
        },
      },
      {
        ...extraColumnProps,
        colId: "download",
        headerName: t("common:download"),
        headerComponentParams: {
          column: {
            id: "download",
          },
        } as DataTableAgHeaderComponentParams,
        cellRenderer: ({
          data,
        }: ICellRendererParams<GenericTableRow, string, DataTableContext>) => {
          const documentId = documentIdForRow(data);
          return documentId ? (
            <DocumentDownloadLink
              documentId={documentId}
              colorScheme="gray"
              aria-label="icon-button"
            >
              <FileDownloadIcon w="20px" h="20px" />
            </DocumentDownloadLink>
          ) : undefined;
        },
      },
    ],
    [t],
  );

  const getColumnDef = useCallback<GetColumnDefFn>(
    (props) =>
      match(props.column.id)
        .with("report_name", () => ({ pinned: false }))
        .otherwise(() => undefined),
    [],
  );

  return (
    <>
      <DataTable
        data={data}
        extraColumns={extraColumns}
        getColumnDef={getColumnDef}
        size="compact"
      />
      {data.rows.length && !!currentReport ? (
        <HistoricalReportsModal
          ranOn={currentReport.ranOn}
          parameters={currentReport.parameters}
          ranBy={currentReport.ranBy}
          reportType={currentReport.type}
          onCloseComplete={() => setCurrentReport(null)}
        />
      ) : null}
    </>
  );
};

// FIXME: Use row metadata
const reportInfoFromRow = (
  row: GenericTableRow | undefined,
): ReportInfo | undefined =>
  row
    ? {
        ranBy:
          (row.cells["report_run_by"].value as TextTableCellValue).text || "",
        ranOn:
          (row.cells["report_date_run"].value as TextTableCellValue).text || "",
        type: (row.cells["report_name"].value as TextTableCellValue).text || "",
        parameters: row.metadata as ReportingRunParametersRowMetadata,
      }
    : undefined;

// FIXME: Use row metadata
const documentIdForRow = (
  row: GenericTableRow | undefined,
): string | undefined | null =>
  (row?.cells["document_id"].value as TextTableCellValue).text;
